import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, FileText, Users, Webhook, Building2, ShieldCheck, LogOut, ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '../auth/AuthProvider'
import { useActiveIntegrator } from '../auth/active-integrator'
import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  systemAdminOnly?: boolean
}

const NAV: NavItem[] = [
  { to: '/',            label: 'Dashboard',   icon: LayoutDashboard },
  { to: '/invoices',    label: 'Invoices',    icon: FileText        },
  { to: '/merchants',   label: 'Merchants',   icon: Users           },
  { to: '/callbacks',   label: 'Callbacks',   icon: Webhook         },
  { to: '/integrators', label: 'Integrators', icon: Building2,       systemAdminOnly: true },
  { to: '/audit',       label: 'Audit log',   icon: ShieldCheck,     systemAdminOnly: true },
]

function initials(email: string): string {
  const name = email.split('@')[0] ?? ''
  const parts = name.split(/[._-]/).filter(Boolean)
  const first = parts[0]?.[0] ?? email[0] ?? '?'
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}

export function Layout() {
  const { session, signOut } = useAuth()
  const active = useActiveIntegrator()
  const navigate = useNavigate()
  if (!session) return null

  const visibleNav = NAV.filter((n) => !n.systemAdminOnly || session.isSystemAdmin)

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="flex w-60 shrink-0 flex-col border-r bg-card">
        <div className="flex h-14 items-center gap-2 border-b px-4">
          <div className="h-7 w-7 rounded bg-primary" aria-hidden />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">{session.integratorName}</span>
            <span className="text-xs text-muted-foreground">reSet Admin</span>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 p-2">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <Separator />
        <div className="p-3 text-xs text-muted-foreground">
          {session.isSystemAdmin ? 'System admin' : `Scoped to ${session.integratorSlug}`}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b bg-card px-6">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{session.integratorName}</span>
            {session.isSystemAdmin && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                system
              </span>
            )}
            {active.isOverride && (
              <Badge variant="secondary" className="gap-1 py-0.5">
                operating as: {active.integratorName}
                <button
                  type="button"
                  aria-label="Clear active integrator"
                  onClick={() => active.clearActive()}
                  className="rounded hover:bg-muted-foreground/20"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initials(session.email)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-sm font-medium">{session.email}</span>
                <span className="text-xs text-muted-foreground capitalize">{session.role}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm">{session.email}</span>
                  <span className="text-xs text-muted-foreground">
                    {session.integratorName} · {session.role}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await signOut()
                  navigate('/login', { replace: true })
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
